import {
  RouterOptions,
  RouteRecordRaw,
  createWebHistory,
  createWebHashHistory,
  RouteLocationRaw,
  RouteLocation,
  RouteLocationNormalized,
  NavigationGuardNext
} from 'vue-router'

import AuthBy from '@web/platform.v3/views/auth/auth-by.vue'
import Token from '@web/common/token'
import i18n from '@web/plugins/i18n'
import { isRegistrationEnable } from '@web/common/ExternalConfig/getters'
import { useStore } from '@web/store'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import { URL_SLUG } from '@web/platform.v3/onboardings/urlSlugs'

/**
 * Для маршрутов с обязательной регистрацией установлен параметр `requiresAuth: false`
 * Для маршрутов доступных только гостям установлен параметр `guest: true`
 * @type {RouterOptions}
 */

let base = process.env.NODE_ENV === 'development' ? '/wds/' : '/class/'
if (process.env.PLATFORM) {
  base = '/'
}
if (process.env.VUE_APP_ROUTER_BASE) {
  base = process.env.VUE_APP_ROUTER_BASE
}

const store = useStore()

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: PAGE_NAME.LOGIN,
    component: AuthBy,
    meta: {
      guest: true,
      title: i18n.global.t('Login')
    }
  },
  {
    path: '/signup',
    name: PAGE_NAME.SIGNUP,
    component: () => import(/* webpackChunkName: 'base' */ '../views/Auth/SignUp.vue'),
    meta: {
      guest: true,
      title: i18n.global.t('signUp')
    },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      return isRegistrationEnable() ? next() : next({ name: PAGE_NAME.LOGIN })
    }
  },
  {
    path: '/auto-login',
    name: PAGE_NAME.AUTO_LOGIN,
    redirect: (to: RouteLocation): RouteLocationRaw => {
      if (to.query.token) {
        store.dispatch('logout')
        Token.set(String(to.query.token))
        if (to.query.returnUrl) {
          window.location.href = to.query.returnUrl.toString()
        } else {
          return { name: PAGE_NAME.HOME }
        }
      }

      return { name: PAGE_NAME.LOGIN }
    }
  },
  {
    path: '/reset',
    name: PAGE_NAME.ACCESS_RECOVERY,
    component: () => import(/* webpackChunkName: 'base' */ '../views/Auth/AccessRecovery.vue'),
    meta: {
      guest: true,
      title: i18n.global.t('restoringPassword')
    }
  },
  {
    path: '/user',
    name: PAGE_NAME.USER,
    meta: {
      requiresAuth: true
    },
    // Убираем возможность попасть на старую страницу
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.HOME }
    }
  },
  {
    path: '/resetPassword',
    name: PAGE_NAME.RESET_PASSWORD,
    component: () => import(/* webpackChunkName: 'auth' */ '../views/Auth/ResetPasswordNew.vue'),
    meta: {
      title: 'Forced password change'
    },
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.HOME }
    }
  },
  {
    path: '/emailVerification',
    name: PAGE_NAME.EMAIL_VERIFICATION,
    component: () => import(/* webpackChunkName: 'auth' */ '../views/Auth/EmailVerification.vue'),
    meta: {
      requiresAuth: true,
      title: 'Email verification'
    }
  },
  {
    path: '/phoneVerification',
    name: PAGE_NAME.PHONE_VERIFICATION,
    component: () => import(/* webpackChunkName: 'auth-onboarding' */ '@web/platform.v3/views/auth/onboarding/OnboardingHost.vue'),
    meta: {
      requiresAuth: true,
      title: 'Phone verification'
    }
  },
  {
    path: '/updateProfile',
    name: PAGE_NAME.UPDATE_PROFILE,
    component: () => import(/* webpackChunkName: 'auth-onboarding' */ '@web/platform.v3/views/auth/onboarding/OnboardingHost.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('crumb.editInformation')
    }
  },
  {
    path: '/onboard/:name',
    redirect: to => ({ name: PAGE_NAME.ONBOARDING, params: { name: to.params.name, step: URL_SLUG.PROFILE_UPDATE } })
  },
  {
    path: '/onboard/:name/:step(\\d+_[a-z_]+)',
    name: PAGE_NAME.ONBOARDING,
    component: () => import(/* webpackChunkName: 'auth-onboarding' */ '@web/platform.v3/views/auth/onboarding/OnboardingHost.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('onboardingStep1')
    }
  },
  {
    // legacy redirect: /onboard/:name/:step(\d+) → resolves to first step's urlSlug
    path: '/onboard/:name/:step(\\d+)',
    redirect: to => ({ name: PAGE_NAME.ONBOARDING, params: { name: to.params.name, step: URL_SLUG.PROFILE_UPDATE } })
  },
  {
    path: '/onboarding',
    redirect: { name: PAGE_NAME.ONBOARDING, params: { name: 'inf_sp', step: URL_SLUG.PROFILE_UPDATE } }
  },
  {
    path: '/user/social-networks',
    name: PAGE_NAME.SETTINGS_SOCIAL_NETWORKS,
    component: () => import(/* webpackChunkName: 'socialNetworks' */ '../views/SocialNetworks.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('linkSocialNetworks'),
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ]
    }
  },
  {
    path: '/user/settings/password',
    name: PAGE_NAME.USER_PASSWORD,
    component: () => import(/* webpackChunkName: 'password' */ '../views/EditPassword.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ],
      title: i18n.global.t('changePassword')
    },
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.HOME }
    }
  },
  {
    path: '/user/settings/notifications',
    name: PAGE_NAME.SETTINGS_NOTIFICATIONS,
    component: () => import(/* webpackChunkName: 'settingsNotifications' */ '../views/Notifications.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ],
      title: 'Notification settings'
    }
  },
  {
    path: '/user/settings/reminders',
    name: PAGE_NAME.SETTINGS_REMINDERS_TRAINING,
    component: () => import(/* webpackChunkName: 'settingsReminders' */ '@web/views/RemindTrainings.vue'),
    meta: {
      requiresAuth: true,
      bottomMenu: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ],
      title: 'Training Reminders'
    }
  },
  {
    path: '/user/settings/reminders/new',
    name: PAGE_NAME.SETTINGS_NEW_REMINDER_TRAINING,
    component: () => import(/* webpackChunkName: 'settingsReminders' */ '@web/views/RemindTraining.vue'),
    meta: {
      requiresAuth: true,
      bottomMenu: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        },
        {
          name: PAGE_NAME.SETTINGS_REMINDERS_TRAINING,
          title: i18n.global.t('reminderTraining')
        }
      ],
      title: 'Training Reminder'
    }
  },
  {
    path: '/user/settings/reminders/:id',
    name: PAGE_NAME.SETTINGS_REMINDER_TRAINING,
    component: () => import(/* webpackChunkName: 'settingsReminders' */ '@web/views/RemindTraining.vue'),
    meta: {
      requiresAuth: true,
      bottomMenu: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        },
        {
          name: PAGE_NAME.SETTINGS_REMINDERS_TRAINING,
          title: i18n.global.t('reminderTraining')
        }
      ],
      title: 'Training Reminder'
    }
  },
  {
    path: '/user/edit',
    name: PAGE_NAME.EDIT_PROFILE,
    component: () => import(/* webpackChunkName: 'userEdit' */ '../views/EditUserInfo.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('crumb.editInformation'),
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ]
    }
  },
  {
    path: '/user/settings',
    name: PAGE_NAME.SETTINGS,
    component: () => import(/* webpackChunkName: 'userSettings' */ '../views/Settings.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('Settings')
    },
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.SETTINGS_REMINDERS_TRAINING }
    }
  },
  {
    path: '/user/subscriptions',
    name: PAGE_NAME.SUBSCRIPTIONS,
    component: () => import(/* webpackChunkName: 'subscriptions' */ '../views.v2/Subscriptions.vue'),
    meta: {
      requiresAuth: true,
      // Разрешаем автологин по ?token из URL только на этой странице (см. middleware).
      allowUrlToken: true,
      title: i18n.global.t('Subscriptions'),
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ]
    }
  },
  {
    path: '/user/installments',
    name: PAGE_NAME.INSTALLMENTS,
    component: () => import(/* webpackChunkName: 'Installments' */ '../views.v2/Installments.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('installments.shortTitle'),
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ]
    }
  },
  {
    path: '/subscriptions',
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.SUBSCRIPTIONS, params: { warning: 'true' } }
    }
  },
  {
    // Точка входа из писем ретеншена: /user/subscription/:id/cancel?token=TOKEN.
    // Редиректим на список подписок с cancel_id (→ запуск оффбординга chain 2),
    // ПРОКИДЫВАЯ token дальше: автологин по нему сработает уже на /user/subscriptions
    // (там meta.allowUrlToken: true, см. middleware), который затем вычистит token из URL.
    path: '/user/subscriptions/:id/cancel',
    redirect: (to: RouteLocation): RouteLocationRaw => ({
      name: PAGE_NAME.SUBSCRIPTIONS,
      query: { ...to.query, cancel_id: String(to.params.id) }
    })
  },
  {
    path: '/user/subscription/:id',
    name: PAGE_NAME.SUBSCRIPTION,
    component: () => import(/* webpackChunkName: 'subscription' */ '../views.v2/Subscription.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('Subscription'),
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        },
        {
          name: PAGE_NAME.SUBSCRIPTIONS,
          title: i18n.global.t('Subscriptions')
        }
      ]
    }
  },
  {
    path: '/user/settings/cookie',
    name: PAGE_NAME.SETTINGS_COOKIE_POLICY,
    component: () => import(/* webpackChunkName: 'settingsCookiePolicy' */ '../views.v2/CookiePolicy.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ],
      title: 'Cookie Policy'
    }
  },
  {
    path: '/user/settings/user-payment-methods',
    name: PAGE_NAME.SETTINGS_USER_PAYMENT_METHODS,
    component: () => import(/* webpackChunkName: 'userPaymentMethods' */ '../views.v2/UserPaymentMethods.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.SETTINGS,
          title: i18n.global.t('Settings')
        }
      ],
      title: 'User Payment Methods'
    }
  },
  {
    path: '/delete',
    name: PAGE_NAME.DELETE,
    redirect: (to: RouteLocation): RouteLocationRaw => {
      store.dispatch('user/delete')
      store.dispatch('logout')
      if (to.query.returnUrl) {
        store.commit('setReturnUrl', String(to.query.returnUrl))
      }

      return { name: PAGE_NAME.LOGIN }
    }
  },
  {
    path: '/logout',
    name: PAGE_NAME.LOGOUT,
    redirect: (to: RouteLocation): RouteLocationRaw => {
      store.dispatch('logout')
      if (to.query.returnUrl) {
        store.commit('setReturnUrl', String(to.query.returnUrl))
      }
      return { name: PAGE_NAME.LOGIN }
    }
  },
  {
    path: '/user/setting',
    name: PAGE_NAME.USER_SETTING,
    component: () => import(/* webpackChunkName: 'userSetting' */ '../views/Setting.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('menu')
    }
  },
  {
    path: '/home',
    name: PAGE_NAME.HOME,
    component: () => import(/* webpackChunkName: 'home' */ '../views.v2/Home.vue'),
    meta: {
      requiresAuth: true,
      title: 'Home page',
      savePosition: true
    }
  },
  {
    path: '/courses',
    name: PAGE_NAME.HOME_COURSES,
    component: () => import(/* webpackChunkName: 'home-courses' */ '../views.v2/HomeAll.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('myCourses')
    }
  },
  {
    path: '/events',
    name: PAGE_NAME.HOME_EVENTS,
    component: () => import(/* webpackChunkName: 'home-events' */ '../views.v2/HomeAll.vue'),
    meta: {
      requiresAuth: true,
      title: 'Events'
    }
  },
  {
    path: '/catalog',
    name: PAGE_NAME.HOME_CATALOG,
    component: () => import(/* webpackChunkName: 'home-catalog' */ '../views.v2/HomeAll.vue'),
    meta: {
      requiresAuth: true,
      title: 'Course catalog'
    }
  },
  {
    path: '/course',
    name: PAGE_NAME.COURSES,
    meta: {
      requiresAuth: true,
      title: i18n.global.t('myCourses')
    },
    // Убираем возможность попасть на старую страницу
    redirect: (): RouteLocationRaw => {
      return { name: PAGE_NAME.HOME }
    }
  },
  {
    path: '/course/:streamId/stages',
    redirect: (to: RouteLocation): RouteLocationRaw => {
      return { name: PAGE_NAME.CALENDAR, params: to.params }
    }
  },
  {
    path: '/course/:streamId/calendar',
    name: PAGE_NAME.CALENDAR,
    component: () => import(/* webpackChunkName: 'calendar' */ '../views.v2/Calendar.vue'),
    meta: {
      requiresAuth: true,
      savePosition: true,
      title: i18n.global.t('scheduleCourse')
    },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      return to.params.streamId ? next() : next({ name: PAGE_NAME.COURSES })
    }
  },
  {
    path: '/course/:streamId/freeze',
    name: PAGE_NAME.COURSE_FREEZE,
    meta: {
      requiresAuth: true,
      savePosition: true,
      title: i18n.global.t('freezeCourse'),
      crumbs: [
        {
          name: PAGE_NAME.CALENDAR,
          title: i18n.global.t('scheduleCourse')
        }
      ]
    },
    // TODO Пока не показывать заморозку
    redirect: (to: RouteLocation): RouteLocationRaw => {
      return { name: PAGE_NAME.CALENDAR, params: to.params }
    },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      return to.params.streamId ? next() : next({ name: PAGE_NAME.COURSES })
    }
  },
  {
    path: '/course/:streamId/stages/:stageId/days/:dayId',
    redirect: (to: RouteLocation): RouteLocationRaw => {
      return { name: PAGE_NAME.DAY, params: to.params }
    }
  },
  {
    path: '/course/:streamId/calendar/:dayNumber',
    name: PAGE_NAME.DAY,
    component: () => import(/* webpackChunkName: 'day' */ '../views.v2/Day.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.CALENDAR,
          title: i18n.global.t('scheduleCourse')
        }
      ],
      title: i18n.global.t('Day')
    },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      const { streamId } = to.params
      if (!streamId) {
        return next({ name: PAGE_NAME.COURSES })
      }

      return next()
    }
  },
  {
    path: '/homework',
    name: PAGE_NAME.HOMEWORKS,
    component: () => import(/* webpackChunkName: 'homeworks' */ '../views/Homeworks.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('homeworks')
    }
  },
  {
    path: '/course/:streamId/homeworks',
    name: PAGE_NAME.HOMEWORKS_STREAM,
    component: () => import(/* webpackChunkName: 'homeworks-stream' */ '@web/platform.v3/views/HomeworksByStream.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('homeworks')
    },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      return to.params.streamId ? next() : next({ name: PAGE_NAME.COURSES })
    }
  },
  {
    path: '/course/:streamId/homework/:homeworkId',
    name: PAGE_NAME.HOMEWORK,
    component: () => import(/* webpackChunkName: 'homework' */ '../views.v2/Homework.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.HOMEWORKS,
          title: i18n.global.t('homeworks')
        }
      ],
      title: i18n.global.t('homework')
    }
  },
  {
    path: '/chat',
    name: PAGE_NAME.CHATS,
    component: () => import(/* webpackChunkName: 'chats' */ '../views/Chats.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.CHATS,
          title: i18n.global.t('messages')
        }
      ],
      title: i18n.global.t('messages')
    }
  },
  {
    path: '/chat/:chatId',
    name: PAGE_NAME.CHAT,
    component: () => import(/* webpackChunkName: 'chat' */ '../views/Chats.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.CHATS,
          title: i18n.global.t('messages')
        }
      ],
      title: i18n.global.t('chat')
    }
  },
  {
    path: '/course/:streamId/chat/:chatId',
    name: PAGE_NAME.CHAT_STREAM,
    component: () => import(/* webpackChunkName: 'chat-stream' */ '@web/platform.v3/views/ChatByStream.vue'),
    meta: {
      requiresAuth: true,
      crumbs: [
        {
          name: PAGE_NAME.CHATS,
          title: i18n.global.t('messages')
        }
      ],
      title: i18n.global.t('chat')
    }
  },
  {
    path: '/videoconsultations',
    name: PAGE_NAME.VIDEO_CONSULTATIONS,
    component: () => import(/* webpackChunkName: 'videoConsultations' */ '../views/VideoConsultations.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('videoConsultations.VideoConsultations')
    }
  },
  {
    path: '/videoconsultations/:id',
    name: PAGE_NAME.VIDEO_CONSULTATION,
    component: () => import(/* webpackChunkName: 'videoConsultation' */ '../views/VideoConsultationInfo.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('videoConsultations.VideoConsultation')
    }
  },
  {
    path: '/course/:streamId/videoconsultations',
    name: PAGE_NAME.VIDEO_CONSULTATIONS_STREAM,
    component: () => import(/* webpackChunkName: 'videoConsultations' */ '@web/platform.v3/views/VideoConsultationsByStream.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('videoConsultations.VideoConsultations')
    }
  },
  {
    path: '/course/:streamId/videoconsultations/:id',
    name: PAGE_NAME.VIDEO_CONSULTATION_STREAM,
    component: () => import(/* webpackChunkName: 'videoConsultation' */ '@web/platform.v3/views/VideoConsultationInfoByStream.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('videoConsultations.VideoConsultation')
    }
  },
  {
    path: '/support/chat',
    name: PAGE_NAME.SUPPORT_CHAT,
    component: () => import(/* webpackChunkName: 'supportChat' */ '@web/platform.v3/views/CareChat.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('careChat.title')
    }
  },
  {
    path: '/support/:categoryId/:streamId',
    name: PAGE_NAME.SUPPORT_CATEGORY_STREAM,
    component: () => import(/* webpackChunkName: 'supportCategoryStream' */ '../views.v2/Support.vue'),
    meta: {
      requiresAuth: true,
      title: `${i18n.global.t('techSupport')} - ${i18n.global.t('myCourses')}`
    }
  },
  {
    path: '/support/:categoryId',
    name: PAGE_NAME.SUPPORT_CATEGORY,
    component: () => import(/* webpackChunkName: 'supportCategory' */ '../views.v2/Support.vue'),
    meta: {
      requiresAuth: true,
      title: `${i18n.global.t('techSupport')} - category`
    }
  },
  {
    path: '/support',
    name: PAGE_NAME.SUPPORT,
    component: () => import(/* webpackChunkName: 'support' */ '../views.v2/Support.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('techSupport')
    }
  },
  {
    path: '/banners',
    name: PAGE_NAME.BANNERS,
    component: () => import(/* webpackChunkName: 'banners' */ '../views/Banners.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('Banner')
    }
  },

  {
    path: '/user/points',
    name: PAGE_NAME.POINTS,
    component: () => import(/* webpackChunkName: 'points' */ '../views.v2/Points.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('Points')
    }
  },
  {
    path: '/user/points/history',
    name: PAGE_NAME.POINTS_HISTORY,
    component: () => import(/* webpackChunkName: 'pointsHistory' */ '../views.v2/PointsHistory.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('History'),
      crumbs: [
        {
          name: PAGE_NAME.POINTS,
          title: i18n.global.t('Points')
        }
      ]
    }
  },
  {
    path: '/course/:streamId/materials',
    name: PAGE_NAME.MATERIALS,
    component: () => import(/* webpackChunkName: 'materials' */ '../views.v2/Materials.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('Materials')
    }
  },
  {
    path: '/new-course/:courseId',
    name: PAGE_NAME.NEW_COURSE,
    component: () => import(/* webpackChunkName: 'new-course' */ '../views.v2/ShortCourse/index.vue'),
    meta: {
      requiresAuth: true,
      title: i18n.global.t('course')
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: PAGE_NAME.NOT_FOUND,
    component: () => import('../views/404.vue'),
    meta: {
      requiresAuth: false
    }
  }
]
const config: RouterOptions = {
  history: createWebHistory(base),
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    return { left: 0, top: 0 }
  }
}

export default config
